// Flappy — flattere durch die Säulen. Blocky-Vogel & Voxel-Säulen,
// komplett prozedural gezeichnet.
import { useRef, useEffect } from "react";
import {
  drawSprite,
  drawTexturedBlock,
  drawBlockText,
  drawCoin,
  setupCanvas,
} from "./pixelArt";

const H = 640;
const BASE_ASPECT = 420 / 640;
const GROUND_H = 72;
const BIRD_X = 110;
const BIRD_SIZE = 32;
const GRAVITY = 1500;
const FLAP_VY = -430;
const PIPE_W = 64;
const PIPE_GAP = 175;
const PIPE_SPEED = 160;
const PIPE_INTERVAL = 1.55; // Sekunden

const BIRD_PALETTE = {
  w: "#f5f5f0",
  o: "#d8d8cf",
  b: "#1f2410",
  y: "#f2b632",
  r: "#d93a2b",
};
// Eigenkreation: blockiger weißer Vogel mit gelbem Schnabel
const BIRD_UP = [
  "..wwww..",
  ".wwwwww.",
  ".wwwbww.",
  ".wwwwwyy",
  "woowwwr.",
  "woowwww.",
  ".wwwww..",
  "..y..y..",
];
const BIRD_DOWN = [
  "..wwww..",
  ".wwwwww.",
  ".wwwbww.",
  "wwwwwwyy",
  "wwwwwwr.",
  ".oowwww.",
  ".oowww..",
  "..y..y..",
];

export default function FlappyGame({ onGameOver, onStart, autoStart = false }) {
  const canvasRef = useRef(null);
  const onGameOverRef = useRef(onGameOver);
  onGameOverRef.current = onGameOver;
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;

  useEffect(() => {
    const canvas = canvasRef.current;
    // Spielfeldbreite ans Seitenverhältnis des Containers anpassen: auf dem
    // Desktop sieht man mehr Welt (Säulen kommen von weiter rechts), nichts
    // wird verzerrt — die logische Höhe bleibt immer gleich.
    const availW = canvas.parentElement?.clientWidth || 420;
    const availH = canvas.parentElement?.clientHeight || H;
    const aspect = Math.min(Math.max(availW / availH, BASE_ASPECT), 3.2);
    const W = Math.round(H * aspect);
    const ctx = setupCanvas(canvas, W, H);

    const state = {
      phase: "ready",
      birdY: H / 2,
      vy: 0,
      pipes: [], // {x, gapY, passed, coin}
      spawnTimer: 0,
      score: 0,
      coins: 0,
      scroll: 0,
      lastTime: 0,
    };

    const makePipe = (x) => {
      const margin = 90;
      const gapY =
        margin + Math.random() * (H - GROUND_H - PIPE_GAP - margin * 2);
      // Selten schwebt eine Münze in der Lücke (10 Münzen = 1 gratis Download)
      return { x, gapY, passed: false, coin: Math.random() < 0.066 };
    };

    const reset = () => {
      state.birdY = H / 2 - 40;
      state.vy = 0;
      state.pipes = [];
      // Breite Felder (Desktop) direkt mit Säulen vorbesetzen, damit vor dem
      // Vogel nicht lange Leere ist
      const spacing = PIPE_SPEED * PIPE_INTERVAL;
      for (let x = BIRD_X + 460; x < W - PIPE_W; x += spacing) {
        state.pipes.push(makePipe(x));
      }
      state.spawnTimer = 0.9;
      state.score = 0;
      state.coins = 0;
    };

    const die = () => {
      state.phase = "over";
      if (onGameOverRef.current)
        onGameOverRef.current(state.score, state.coins);
    };

    const update = (dt) => {
      state.scroll += PIPE_SPEED * dt;

      state.vy += GRAVITY * dt;
      state.birdY += state.vy * dt;

      // Decke & Boden
      if (state.birdY < -10) {
        state.birdY = -10;
        state.vy = 0;
      }
      if (state.birdY + BIRD_SIZE > H - GROUND_H) {
        die();
        return;
      }

      // Säulen spawnen
      state.spawnTimer -= dt;
      if (state.spawnTimer <= 0) {
        state.spawnTimer = PIPE_INTERVAL;
        state.pipes.push(makePipe(W + 10));
      }

      // Säulen bewegen & Kollision (leicht verzeihende Hitbox)
      const bx = BIRD_X + 4;
      const bw = BIRD_SIZE - 8;
      const by = state.birdY + 4;
      const bh = BIRD_SIZE - 8;

      for (const p of state.pipes) {
        p.x -= PIPE_SPEED * dt;

        if (!p.passed && p.x + PIPE_W < BIRD_X) {
          p.passed = true;
          state.score++;
        }

        if (bx + bw > p.x && bx < p.x + PIPE_W) {
          if (by < p.gapY || by + bh > p.gapY + PIPE_GAP) {
            die();
            return;
          }
        }

        // Münze in der Lücke einsammeln
        if (p.coin) {
          const cSize = 26;
          const cx = p.x + PIPE_W / 2 - cSize / 2;
          const cy = p.gapY + PIPE_GAP / 2 - cSize / 2;
          if (
            bx + bw > cx &&
            bx < cx + cSize &&
            by + bh > cy &&
            by < cy + cSize
          ) {
            p.coin = false;
            state.coins++;
          }
        }
      }
      state.pipes = state.pipes.filter((p) => p.x > -PIPE_W - 20);
    };

    const drawPipe = (p) => {
      const groundY = H - GROUND_H;
      // Obere Säule
      drawTexturedBlock(ctx, p.x, -8, PIPE_W, p.gapY + 8, "#4e9420", "#6abe30", "#3a7018", 101);
      drawTexturedBlock(ctx, p.x - 5, p.gapY - 24, PIPE_W + 10, 24, "#4e9420", "#6abe30", "#3a7018", 202);
      // Untere Säule
      const bottomY = p.gapY + PIPE_GAP;
      drawTexturedBlock(ctx, p.x, bottomY, PIPE_W, groundY - bottomY + 8, "#4e9420", "#6abe30", "#3a7018", 303);
      drawTexturedBlock(ctx, p.x - 5, bottomY, PIPE_W + 10, 24, "#4e9420", "#6abe30", "#3a7018", 404);
    };

    const draw = () => {
      // Himmel
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "#57b9e8");
      grad.addColorStop(1, "#a4dbf5");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // Wolken (parallax)
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      for (let i = 0; i < 4; i++) {
        const cx = ((i * 160 - state.scroll * 0.25) % (W + 120)) + (i % 2) * 30;
        const x = cx < -100 ? cx + W + 120 : cx;
        const cy = 60 + i * 90;
        ctx.fillRect(x, cy, 72, 16);
        ctx.fillRect(x + 14, cy - 12, 44, 12);
      }

      for (const p of state.pipes) {
        drawPipe(p);
        if (p.coin) {
          drawCoin(ctx, p.x + PIPE_W / 2 - 13, p.gapY + PIPE_GAP / 2 - 13, 26);
        }
      }

      // Boden: Gras auf Erde, scrollend
      const groundY = H - GROUND_H;
      drawTexturedBlock(ctx, 0, groundY, W, GROUND_H, "#8a5a2b", "#a06a35", "#6e4620", 55);
      const tile = 24;
      const off = state.scroll % tile;
      for (let x = -tile; x < W + tile; x += tile) {
        ctx.fillStyle = Math.floor((x + state.scroll) / tile) % 2 === 0 ? "#6abe30" : "#5aa828";
        ctx.fillRect(x - off, groundY, tile, 10);
      }

      // Vogel (Flügel je nach Steig-/Sinkflug)
      const sprite = state.vy < 0 ? BIRD_UP : BIRD_DOWN;
      drawSprite(ctx, sprite, BIRD_PALETTE, BIRD_X, state.birdY, BIRD_SIZE / 8);

      drawBlockText(ctx, `${state.score}`, W / 2, 50, 40);
      drawCoin(ctx, 12, 16, 20);
      drawBlockText(ctx, `${state.coins}`, 48, 27, 20);

      if (state.phase === "ready") {
        ctx.fillStyle = "rgba(10, 20, 30, 0.5)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "FLAPPY", W / 2, H / 2 - 40, 46);
        drawBlockText(ctx, "Tippen / Leertaste zum Flattern", W / 2, H / 2 + 10, 16);
      } else if (state.phase === "over") {
        ctx.fillStyle = "rgba(10, 20, 30, 0.5)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "GAME OVER", W / 2, H / 2 - 40, 40);
        drawBlockText(ctx, `${state.score} Säulen`, W / 2, H / 2 + 5, 24);
        drawBlockText(ctx, "Tippen für Neustart", W / 2, H / 2 + 45, 15);
      }
    };

    let raf;
    const loop = (now) => {
      const dt = Math.min(0.04, (now - state.lastTime) / 1000);
      state.lastTime = now;
      if (state.phase === "running") update(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };

    const action = () => {
      if (state.phase === "running") {
        state.vy = FLAP_VY;
      } else {
        reset();
        state.phase = "running";
        state.vy = FLAP_VY;
        if (onStartRef.current) onStartRef.current();
      }
    };

    const onKeyDown = (e) => {
      if (e.key === " " || e.key === "Enter" || e.key === "ArrowUp") {
        e.preventDefault();
        action();
      }
    };
    const onPointerDown = (e) => {
      e.preventDefault();
      action();
    };

    window.addEventListener("keydown", onKeyDown);
    canvas.addEventListener("pointerdown", onPointerDown);

    state.lastTime = performance.now();
    if (autoStart) action(); // Start-Button auf der Seite: direkt losspielen
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKeyDown);
      canvas.removeEventListener("pointerdown", onPointerDown);
    };
  }, [autoStart]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full touch-none select-none"
      style={{ imageRendering: "pixelated", objectFit: "contain" }}
    />
  );
}
